if (!customElements.get('subscription-spin-wheel')) {
  customElements.define(
    'subscription-spin-wheel',
    class SubscriptionSpinWheel extends HTMLElement {
      static STORAGE_KEY = 'subscription-popup-spin-result-v2';

      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.spinning = false;
        this.wheel = this.querySelector('[data-spin-wheel]');
        this.copyBtn = this.querySelector('[data-spin-copy]');
        this.copyLabel = this.copyBtn?.dataset.copyLabel?.trim() || 'Copy code';
        this.spinBtn = this.querySelector('[data-action="spin"]');
        this.claimedBtn = this.querySelector('[data-action="claimed"]');
        this.claimedFallback =
          this.claimedBtn?.textContent?.trim() ||
          this.dataset.claimedLabel?.trim() ||
          'ALREADY CLAIMED';
        this.triggers = Array.from(this.querySelectorAll('[data-spin-trigger]'));
        this.subscribeGate = this.querySelector('[data-spin-subscribe-gate]');
        this.playActions = this.querySelector('[data-spin-play-actions]');
        this.dismissBtn = this.querySelector('[data-spin-dismiss]');
        this.prizes = this.parsePrizes();
        this.pointerAngle = this.readPointerAngle();

        this.triggers.forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            this.spin();
          });
        });

        this.copyBtn?.addEventListener('click', (event) => {
          event.preventDefault();
          this.copyCode();
        });

        this.dismissBtn?.addEventListener('click', (event) => {
          event.preventDefault();
          const popup = this.closest('subscription-popup');
          popup?.hide?.();
        });
      }

      restoreSavedResult() {
        const saved = this.readResult();
        if (saved) this.applyResult(saved, { animate: false });
      }

      parsePrizes() {
        try {
          const raw = this.getAttribute('data-prizes') || '[]';
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      readResult() {
        try {
          const raw = localStorage.getItem(SubscriptionSpinWheel.STORAGE_KEY);
          if (!raw) return null;
          return JSON.parse(raw);
        } catch (error) {
          return null;
        }
      }

      writeResult(result) {
        try {
          localStorage.setItem(SubscriptionSpinWheel.STORAGE_KEY, JSON.stringify(result));
        } catch (error) {}
      }

      pickPrizeIndex() {
        const weights = this.prizes.map((prize) => {
          const weight = Number(prize.weight);
          return Number.isFinite(weight) && weight > 0 ? weight : 1;
        });
        const total = weights.reduce((sum, weight) => sum + weight, 0);
        if (total <= 0) return 0;
        let ticket = Math.random() * total;
        for (let i = 0; i < weights.length; i += 1) {
          ticket -= weights[i];
          if (ticket <= 0) return i;
        }
        return weights.length - 1;
      }

      readPointerAngle() {
        const fromData = Number(this.dataset.pointerAngle);
        if (Number.isFinite(fromData)) return fromData;
        const fromCss = Number.parseFloat(
          getComputedStyle(this).getPropertyValue('--spin-pointer-angle')
        );
        return Number.isFinite(fromCss) ? fromCss : 40;
      }

      /**
       * Rotate so the winning segment center lands under the fixed pointer.
       * Segment 0 starts at top (0deg) and goes clockwise — same as conic wedges.
       * Pointer seat is --spin-pointer-angle / data-pointer-angle (deg from top).
       */
      rotationForIndex(index, spins = 0) {
        const count = this.prizes.length || 1;
        const segment = 360 / count;
        const center = index * segment + segment / 2;
        const offset = Number.isFinite(this.pointerAngle) ? this.pointerAngle : this.readPointerAngle();
        return spins * 360 - center + offset;
      }

      copyCode() {
        if (!this.copyBtn) return;
        const code = this.copyBtn.dataset.code;
        if (!code) return;

        const showFeedback = () => {
          const copiedText = this.dataset.copiedText || 'Copied';
          const idleText = `${this.copyLabel}: ${code}`.trim();
          this.copyBtn.classList.add('is-copy-flash');
          this.copyBtn.textContent = copiedText;
          clearTimeout(this._copyFlashTimer);
          this._copyFlashTimer = setTimeout(() => {
            this.copyBtn.classList.remove('is-copy-flash');
            if (this.copyBtn.dataset.code === code) {
              this.copyBtn.textContent = idleText;
            }
          }, 1400);
        };

        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(code).then(showFeedback).catch(showFeedback);
        } else {
          showFeedback();
        }
      }

      setActionVisible(action) {
        if (this.spinBtn) this.spinBtn.hidden = action !== 'spin';
        if (this.copyBtn) this.copyBtn.hidden = action !== 'copy';
        if (this.claimedBtn) this.claimedBtn.hidden = action !== 'claimed';
        this.triggers.forEach((btn) => {
          btn.disabled = action !== 'spin';
        });
      }

      applyResult(result, { animate = false } = {}) {
        if (!result || !this.wheel) return;
        const index = Number(result.index);
        if (!Number.isFinite(index) || index < 0 || index >= this.prizes.length) return;

        const spins = animate ? result.spins || 6 : 0;
        const rotation = this.rotationForIndex(index, spins);
        if (!animate) {
          this.wheel.style.transition = 'none';
          this.wheel.style.transform = `rotate(${rotation}deg)`;
          void this.wheel.offsetWidth;
          this.wheel.style.transition = '';
        }

        const hasCode = !result.isMystery && Boolean(result.code);
        if (hasCode) {
          if (this.copyBtn) {
            this.copyBtn.dataset.code = result.code;
            this.copyBtn.textContent = `${this.copyLabel}: ${result.code}`.trim();
          }
          this.setActionVisible('copy');
        } else {
          if (this.claimedBtn) {
            this.claimedBtn.textContent = (result.label || this.claimedFallback).trim();
          }
          this.setActionVisible('claimed');
        }

        this.classList.add('is-claimed');
      }

      /**
       * @param {'subscribe'|'play'} mode
       * subscribe = email + Subscribe button; play = Spin / Copy / Claimed
       */
      setMode(mode) {
        const isSubscribe = mode === 'subscribe' && Boolean(this.subscribeGate);
        this.classList.toggle('subscription-spin-wheel--subscribe-gate', isSubscribe);
        if (this.subscribeGate) {
          this.subscribeGate.hidden = !isSubscribe;
        }
        if (this.playActions) {
          this.playActions.hidden = isSubscribe;
        }
        // Gate: hub must not spin; play: restore unless already claimed.
        this.triggers.forEach((btn) => {
          btn.disabled = isSubscribe || this.classList.contains('is-claimed');
        });
      }

      spin() {
        if (
          this.classList.contains('subscription-spin-wheel--subscribe-gate') ||
          this.spinning ||
          this.classList.contains('is-claimed') ||
          !this.prizes.length
        ) {
          return;
        }
        const existing = this.readResult();
        if (existing) {
          this.applyResult(existing, { animate: false });
          return;
        }

        const index = this.pickPrizeIndex();
        const prize = this.prizes[index];
        const spins = 5 + Math.floor(Math.random() * 3);
        const rotation = this.rotationForIndex(index, spins);
        const result = {
          index,
          spins,
          id: prize.id,
          label: prize.label,
          isMystery: Boolean(prize.isMystery),
          code: prize.isMystery ? '' : prize.code || '',
        };

        this.spinning = true;
        this.classList.add('is-spinning');
        this.triggers.forEach((btn) => {
          btn.disabled = true;
        });

        if (window.FmAnalytics && typeof window.FmAnalytics.track === 'function') {
          window.FmAnalytics.track('spin_wheel_spin');
        }

        this.wheel.style.transform = `rotate(${rotation}deg)`;

        let settled = false;
        const settle = () => {
          if (settled) return;
          settled = true;
          this.wheel.removeEventListener('transitionend', onEnd);
          this.spinning = false;
          this.classList.remove('is-spinning');
          this.writeResult(result);
          this.applyResult(result, { animate: false });
          this.closest('subscription-popup')?.markSpinCompleted?.();
        };
        const onEnd = (event) => {
          if (event.target !== this.wheel) return;
          settle();
        };

        this.wheel.addEventListener('transitionend', onEnd);
        setTimeout(settle, 4600);
      }
    }
  );
}

if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat([
          'data-enable-trigger-delay',
          'data-trigger-open-delay',
          'data-enable-trigger-scroll-depth',
          'data-trigger-scroll-depth',
          'data-repeat-open',
        ]);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.addClassByUA()

        this.enableTriggerDelay = this.dataset.enableTriggerDelay === 'true';
        this.enableTriggerScrollDepth = this.dataset.enableTriggerScrollDepth === 'true';
        this.enableSpinWheel = this.dataset.enableSpinWheel === 'true';
        this.repeatOpen = this.dataset.repeatOpen;
        this.cookieName = 'sleektheme:popup';
        this.teaser = document.getElementById(`SubscriptionTeaser-${this.dataset.section}`);
        this.teaserDismissKey = `timephoria:subscription-teaser:${this.dataset.section}`;
        this.teaserDismissed = false;
        this.autoOpened = false;
        this.scrollDepthHandler = null;

        this.setupTeaserDismiss();

        // 订阅表单刚提交成功：进抽奖态；不要用 localStorage 一打开就跳过订阅 gate
        if (this.querySelector('.subscription-popup__spin-success-marker')) {
          this.enterSpinWheelMode({ play: true });
        }

        window.FoxTheme.DOMready(this.handleOpen.bind(this));

        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.btn-apply-coupon-code',
          handler: async (e, target) => {
            e.preventDefault();
            const code = target.dataset.code;
            if (code) this.copyToClipboard(code, target);
          },
        });

        window.addEventListener('resize', () => {
          setTimeout(() => {
            this.addClassByUA()
          })
        })

        this.addEventListener('drawer:handleAfterHide', () => {
          this.releaseBackgroundScroll();
          // Always leave spin mode on close so Hub / auto-open shows the form again
          // (unless the next open explicitly requests spin or has a success marker).
          this.exitSpinWheelMode();
        });

        this.addEventListener('drawer:handleAfterShow', () => {
          this.trapBackgroundScroll();
        });
      }

      /**
       * Never use theme body.modal-show / html overflow locks — both paint the
       * page black behind the overlay on Windows and mobile browsers.
       * Background scroll is blocked via overlay touch/wheel listeners instead.
       */
      get isLockingNeeded() {
        return false;
      }

      trapBackgroundScroll() {
        this.releaseBackgroundScroll();
        this._bgTouchStartY = 0;

        this._bgScrollTouchStart = (event) => {
          this._bgTouchStartY = event.touches?.[0]?.clientY || 0;
        };

        this._bgScrollTouch = (event) => {
          if (this.shouldAllowPopupScroll(event.target, event.touches?.[0]?.clientY - this._bgTouchStartY)) {
            return;
          }
          event.preventDefault();
        };

        this._bgScrollWheel = (event) => {
          if (this.shouldAllowPopupScroll(event.target, -event.deltaY)) {
            return;
          }
          event.preventDefault();
        };

        document.addEventListener('touchstart', this._bgScrollTouchStart, { passive: true, capture: true });
        document.addEventListener('touchmove', this._bgScrollTouch, { passive: false, capture: true });
        document.addEventListener('wheel', this._bgScrollWheel, { passive: false, capture: true });
      }

      /**
       * Allow scroll only when the gesture is inside a popup scroller that still
       * has room in that direction — otherwise block so the page behind cannot move.
       * @param {EventTarget|null} target
       * @param {number} deltaY positive = finger moved down / content wants to go up
       */
      shouldAllowPopupScroll(target, deltaY) {
        if (!this.open || !target?.closest) return false;
        const panel = target.closest('.drawer__inner');
        if (!panel || !this.contains(panel)) return false;

        const scroller =
          target.closest('.drawer__body, .v-scrollable, .subscription-spin-wheel-shell') || panel;
        if (!this.contains(scroller)) return false;

        const canScroll = scroller.scrollHeight > scroller.clientHeight + 1;
        if (!canScroll) return false;

        const dy = Number(deltaY) || 0;
        if (dy === 0) return true;

        const atTop = scroller.scrollTop <= 0;
        const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
        // dy > 0: pulling down → would scroll content up / reveal above → block at top
        if (dy > 0 && atTop) return false;
        if (dy < 0 && atBottom) return false;
        return true;
      }

      releaseBackgroundScroll() {
        if (this._bgScrollTouchStart) {
          document.removeEventListener('touchstart', this._bgScrollTouchStart, { capture: true });
          this._bgScrollTouchStart = null;
        }
        if (this._bgScrollTouch) {
          document.removeEventListener('touchmove', this._bgScrollTouch, { capture: true });
          this._bgScrollTouch = null;
        }
        if (this._bgScrollWheel) {
          document.removeEventListener('wheel', this._bgScrollWheel, { capture: true });
          this._bgScrollWheel = null;
        }
        // Clear any leftover theme locks from earlier builds / other drawers.
        document.body.classList.remove('modal-show', 'modal-showing');
        document.documentElement.classList.remove('tp-popup-scroll-lock');
        document.body.classList.remove('tp-popup-scroll-lock');
      }

      isSubscriptionPosted() {
        try {
          return localStorage.getItem('subscription-popup-posted_successfully') === 'true';
        } catch (error) {
          return false;
        }
      }

      hasCompletedSpin() {
        if (!this.enableSpinWheel) return false;
        try {
          return Boolean(localStorage.getItem('subscription-popup-spin-result-v2'));
        } catch (error) {
          return false;
        }
      }

      /** After a spin finishes, suppress auto-open on later visits (cookie + result). */
      markSpinCompleted() {
        if (Shopify.designMode) return;
        FoxTheme.utils.setStorage(this.cookieName, { opened: true }, 365);
      }

      /**
       * @param {{ play?: boolean }} [options]
       * play=true → Spin lottery; play=false → email + Subscribe on the same shell.
       */
      enterSpinWheelMode({ play = true } = {}) {
        if (!this.enableSpinWheel || !this.querySelector('subscription-spin-wheel')) return;
        this.classList.add('subscription-popup--spin-wheel');
        const main = this.querySelector('.subscription-popup__main');
        if (main) main.hidden = true;
        // Mobile: same bottom-sheet as subscribe form; desktop: centered modal.
        this.addClassByUA();
        const wheel = this.querySelector('subscription-spin-wheel');
        if (!wheel) return;
        if (play) {
          wheel.setMode?.('play');
          wheel.restoreSavedResult?.();
        } else {
          wheel.setMode?.('subscribe');
        }
      }

      exitSpinWheelMode() {
        this.classList.remove('subscription-popup--spin-wheel');
        const main = this.querySelector('.subscription-popup__main');
        if (main) main.hidden = false;
        const wheel = this.querySelector('subscription-spin-wheel');
        wheel?.setMode?.('play');
        this._forceSpinWheel = false;
        this._skipRepeatOpen = false;
        this.addClassByUA();
      }

      /**
       * Floating-icon entry: open spin wheel in play mode (no subscribe required).
       * Does not mark the subscription popup cookie (keeps auto-subscribe triggers intact).
       */
      showSpinWheel() {
        if (!this.enableSpinWheel || !this.querySelector('subscription-spin-wheel')) return;
        this._forceSpinWheel = true;
        this._skipRepeatOpen = true;
        this.enterSpinWheelMode({ play: true });
        // Already open (e.g. subscribe gate): show() is a no-op and would leave
        // _forceSpinWheel stuck true — clear flags after switching UI.
        if (this.open) {
          this._forceSpinWheel = false;
          this._skipRepeatOpen = false;
          return;
        }
        this.show();
      }

      setupTeaserDismiss() {
        if (!this.teaser) return;

        this.teaserCloseButton = this.teaser.querySelector('[data-subscription-teaser-close]');

        if (!Shopify.designMode) {
          try {
            this.teaserDismissed = sessionStorage.getItem(this.teaserDismissKey) === 'true';
          } catch (error) {
            this.teaserDismissed = false;
          }
        }

        if (this.teaserDismissed) {
          this.teaser.hidden = true;
          return;
        }

        this.teaserCloseButton?.addEventListener('click', (event) => this.dismissTeaser(event));
      }

      dismissTeaser(event) {
        event.preventDefault();
        event.stopPropagation();
        this.teaserDismissed = true;
        this.teaser.hidden = true;

        if (!Shopify.designMode) {
          try {
            sessionStorage.setItem(this.teaserDismissKey, 'true');
          } catch (error) {}
        }
      }

      addClassByUA() {
        if (window.FoxTheme.config.mqlMobile) {
          this.classList.add('drawer--bottom');
          this.classList.remove('modal');
        } else {
          this.classList.add('modal');
          this.classList.remove('drawer--bottom');
        }
      }

      prepareToShow() {
        super.prepareToShow();
        // Drop any leftover theme overflow locks before paint (auto-open black flash).
        document.body.classList.remove('modal-show', 'modal-showing');
        document.documentElement.classList.remove('tp-popup-scroll-lock');
        document.body.classList.remove('tp-popup-scroll-lock');
        // 浮窗 / 订阅成功 → 抽奖；开启转盘时其余打开 → 订阅 gate；未开转盘 → 旧表单
        const openAsPlay =
          this._forceSpinWheel || this.querySelector('.subscription-popup__spin-success-marker');
        if (openAsPlay) {
          this.enterSpinWheelMode({ play: true });
        } else if (this.enableSpinWheel) {
          this.enterSpinWheelMode({ play: false });
        } else {
          this.exitSpinWheelMode();
        }
        if (!this._skipRepeatOpen) {
          this.setRepeatOpen();
        }
        this._forceSpinWheel = false;
        this._skipRepeatOpen = false;
        this.trapBackgroundScroll();
      }

      handleOpen() {
        if (Shopify.designMode || this.teaserDismissed) return;
        const subscriptionSuccessTip = this.querySelector('.subscription-popup__sub-success-tip')
        const spinSuccessMarker = this.querySelector('.subscription-popup__spin-success-marker')
        // 已抽完：刷新后不再自动弹；仍允许订阅成功当次（带 marker）展示转盘结果
        if (this.hasCompletedSpin() && !spinSuccessMarker) {
          return false;
        }
        let savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened && !subscriptionSuccessTip && !spinSuccessMarker) {
          return false;
        }

        if (spinSuccessMarker) {
          this.enterSpinWheelMode({ play: true });
          this.show();
          return;
        }

        if (subscriptionSuccessTip) {
          const isSubmitted = this.isSubscriptionPosted();
          if (isSubmitted) return
          const title = this.querySelector('.subscription-popup__title')
          const subText = this.querySelector('.subscription-popup__text')
          const cta = this.querySelector('.subscription-popup__cta')
          const code = this.querySelector('.subscription-popup__code')

          if (title) {
            title.style.display = 'none'
          }
          if (subText) {
            subText.style.display = 'none'
          }
          if (cta) {
            cta.style.display = 'none'
          }
          if (code) {
            code.style.display = 'none'
          }

          this.show()
          return;
        }

        if (this.enableTriggerDelay) {
          const delaySeconds = Number.parseInt(this.dataset.triggerOpenDelay || '12', 10);
          const delayMs =
            Number.isFinite(delaySeconds) && delaySeconds > 0 ? delaySeconds * 1000 : 12000;
          setTimeout(() => this.showOnce(), delayMs);
        }

        if (this.enableTriggerScrollDepth) {
          this.setupScrollDepthTrigger();
        }
      }

      showOnce() {
        if (this.autoOpened || this.teaserDismissed || this.hasCompletedSpin()) return;
        this.autoOpened = true;
        this.teardownScrollDepthTrigger();
        this.show();
      }

      getScrollDepthPercent() {
        const doc = document.documentElement;
        const scrollable = Math.max(doc.scrollHeight - window.innerHeight, 0);
        // 页面几乎不可滚动时不按“已看完”处理，避免一进页就立刻弹出
        if (scrollable <= 0) return 0;
        const scrollTop =
          window.pageYOffset || doc.scrollTop || document.body.scrollTop || 0;
        return Math.min(100, Math.max(0, (scrollTop / scrollable) * 100));
      }

      setupScrollDepthTrigger() {
        const depthPercent = Number.parseInt(this.dataset.triggerScrollDepth || '50', 10);
        const threshold =
          Number.isFinite(depthPercent) && depthPercent > 0 ? Math.min(depthPercent, 100) : 50;

        this.scrollDepthHandler = () => {
          if (this.getScrollDepthPercent() >= threshold) {
            this.showOnce();
          }
        };

        window.addEventListener('scroll', this.scrollDepthHandler, { passive: true });
        this.scrollDepthHandler();
      }

      teardownScrollDepthTrigger() {
        if (!this.scrollDepthHandler) return;
        window.removeEventListener('scroll', this.scrollDepthHandler);
        this.scrollDepthHandler = null;
      }

      copyToClipboard(code, button) {
        navigator.clipboard.writeText(code);
        button.innerText = button.dataset.copiedText;
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}

if (!customElements.get('popup-tip')) {
  customElements.define(
    'popup-tip',
    class PopupTip extends DrawerComponent {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId
        this.init()
      }

      init() {
        const isSubmitted = localStorage.getItem('subscription-popup-posted_successfully') === 'true';
        if (!isSubmitted) {
          this.classList.remove('hidden')
        } else {
          return
        }
        if (this.sectionId) {
          this.subscriptionPopup = window.document.documentElement.querySelector('#SubscriptionModal-' + this.sectionId)
        }
        if (!this.subscriptionPopup) return
        this.addEventListener('click', function() {
          this.subscriptionPopup?.show()
          this.classList.add('hidden')
        })

        this.subscriptionPopup.addEventListener(this.subscriptionPopup.events.handleAfterHide, () => {
           this.classList.remove('hidden')
        })
      }
    })
}

if (!customElements.get('email-autocomplete')) {
    customElements.define(
    'email-autocomplete',
  class EmailAutocomplete extends HTMLElement {
      constructor() {
        super();
        this.suffixes = [
          'gmail.com', 'outlook.com', 'icloud.com', 'hotmail.com', 'yahoo.com','foxmail.com'
        ];
      }

      connectedCallback() {
        this.classList.add('newsletter-form-container');
        this.input = this.querySelector('input');
        this.clearBtn = this.querySelector('.input__clear'); // 获取清除按钮
        
        if (!this.input) return;

        this.initDatalist();
        this.input.setAttribute('list', this.datalistId);
        
        this.input.addEventListener('input', () => {
          this.handleInput();
          this.toggleClearBtn(); // 实时控制清除按钮显示
        });

        if (this.clearBtn) {
          this.clearBtn.addEventListener('click', () => this.clearInput());
        }
      }

      initDatalist() {
        this.datalistId = `email-list-${Math.random().toString(36).substring(2, 11)}`;
        const datalist = document.createElement('datalist');
        datalist.id = this.datalistId;
        this.appendChild(datalist);
        this.datalist = datalist;
      }

      handleInput() {
        const value = this.input.value;
        const atIndex = value.indexOf('@');
        this.datalist.innerHTML = ''; 

        if (atIndex > -1) {
          const prefix = value.substring(0, atIndex + 1);
          const currentSuffix = value.substring(atIndex + 1).toLowerCase();
          this.suffixes.forEach(suffix => {
            if (currentSuffix === '' || suffix.startsWith(currentSuffix)) {
              const option = document.createElement('option');
              option.value = prefix + suffix;
              this.datalist.appendChild(option);
            }
          });
        }
      }

      // --- 新增：清除逻辑 ---
      toggleClearBtn() {
        if (this.clearBtn) {
          this.clearBtn.style.display = this.input.value.length > 0 ? 'block' : 'none';
        }
      }

      clearInput() {
        this.input.value = '';
        this.toggleClearBtn();
        this.datalist.innerHTML = '';
        this.input.focus();
      }
    }
  )
}
